/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';

import type { Theme } from '@coursera/cds-core';
import { Link, Typography2, breakpoints } from '@coursera/cds-core';

import type {
  EntWebsiteBlockAssemblyFragment,
  EntWebsiteProofPointFragment,
} from 'bundles/ent-website/__generated__/contentfulTypes';
import { FootnoteContext, createAnchor, createReverseAnchor } from 'bundles/ent-website/components/FootnoteContext';

type Props = {
  proofPoints: Array<EntWebsiteProofPointFragment>;
  assembly: Omit<EntWebsiteBlockAssemblyFragment, 'sys'>;
};

function InlineProofPoints({ proofPoints, assembly }: Props) {
  const styles: Record<string, Interpolation<Theme>> = {
    inlineProofPointsCollection: css`
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
    `,

    inlineProofPoint: () => css`
      flex-basis: 50%;
      padding-right: var(--cds-spacing-600);
      margin-bottom: var(--cds-spacing-300);
      ${breakpoints.down('sm')} {
        flex: 1 1 100%;
        padding-right: 0;
      }
    `,

    inlineProofPointKeyMetric: css`
      font-size: 1.75rem !important;
      line-height: 2.25rem !important;
    `,

    inlineProofPointUnit: css`
      font-size: 1rem !important;
    `,

    inlineProofPointUnitLink: () => css`
      a {
        color: var(--cds-color-grey-600) !important;
      }
    `,

    inlineProofPointDescription: () => css`
      margin-top: var(--cds-spacing-50);
    `,

    sourceLink: css`
      margin-left: 2px;
      text-decoration: underline;

      &:hover {
        text-decoration: underline;
      }
    `,
  };

  const { registerFootnote, getFootnote } = React.useContext(FootnoteContext);

  const createFootnoteLink = (key: string) => {
    const entry = getFootnote(key);
    return entry ? (
      <Link id={createReverseAnchor(entry.num)} href={`#${createAnchor(entry.num)}`} css={styles.sourceLink}>
        {entry.num}
      </Link>
    ) : null;
  };

  // collect source links
  React.useEffect(() => {
    for (const pp of proofPoints) {
      if (pp.footnote && pp.slug) {
        registerFootnote(pp.slug, pp.footnote);
      }
    }
  }, [proofPoints, registerFootnote]);

  return (
    <div css={styles.inlineProofPointsCollection}>
      {proofPoints.map(
        (pp) =>
          pp && (
            <div css={styles.inlineProofPoint} key={pp.slug}>
              <Typography2 variant="subtitleMedium" component="div" css={styles.inlineProofPointKeyMetric}>
                {pp.keyMetric}
              </Typography2>

              {pp.unit && (
                <Typography2 variant="subtitleMedium" component="div" css={styles.inlineProofPointUnit}>
                  {pp.unit}
                  {pp.footnote && pp.slug && (
                    <sup css={styles.inlineProofPointUnitLink}>{createFootnoteLink(pp.slug)}</sup>
                  )}
                </Typography2>
              )}
              <Typography2
                component="p"
                variant="bodyPrimary"
                color="supportText"
                css={styles.inlineProofPointDescription}
              >
                {pp.description}
              </Typography2>
            </div>
          )
      )}
    </div>
  );
}

export default InlineProofPoints;
